import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Anchor,
  Badge,
  Breadcrumbs,
  Paper,
  SimpleGrid,
  Table,
  Text,
  Title,
  Stack,
  Accordion,
  Group,
  Box,
  Button,
  ActionIcon,
  Tooltip,
  Loader,
} from '@mantine/core';
import { XIcon, TargetIcon } from 'lucide-react';
import { LoadingView } from '@practist/components';
import {
  useFirm,
  useFirmMatterDetail,
  useMatterTransactions,
} from '@/features/firms/hooks';
import type { TransactionDocument } from '@/features/firms/hooks';
import { useFinixWebhookEvents } from '@/features/finix/hooks';
import { formatRelativeDate } from './dateUtils';
import styles from './Firm.module.css';

interface ExtendedTransferFields {
  updated_at?: string;
  currency?: string;
  failure_code?: string;
  failure_message?: string;
  application?: string;
  statement_descriptor?: string;
  type?: string;
}

interface ExtendedEntityFields {
  business_name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  business_tax_id?: string;
  incorporation_date?: string;
  business_address?: {
    line1?: string;
    line2?: string;
    city?: string;
    region?: string | null;
    postal_code?: string;
    country?: string;
  } | null;
}

interface ExtendedIdentityFields {
  updated_at?: string;
  application?: string;
  entity?: ExtendedEntityFields;
}

interface ExtendedPaymentInstrumentFields {
  fingerprint?: string;
  bank_code?: string;
  country?: string;
  currency?: string;
  updated_at?: string;
  application?: string;
}

const MatterDetail = () => {
  const { firmId, matterId } = useParams();
  const firm = useFirm(firmId);
  const { matter, isLoading: matterLoading } = useFirmMatterDetail(
    firmId,
    matterId,
  );
  const [highlightedPaymentInstruments, setHighlightedPaymentInstruments] =
    useState<Set<string>>(new Set());
  const { webhookEvents } = useFinixWebhookEvents();
  const { transactions, isLoading: transactionsLoading } =
    useMatterTransactions(firmId, matterId);

  const togglePaymentInstrumentHighlight = (instrumentId: string) => {
    setHighlightedPaymentInstruments((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(instrumentId)) {
        newSet.delete(instrumentId);
      } else {
        newSet.add(instrumentId);
      }
      return newSet;
    });
  };

  const clearAllHighlights = () => {
    setHighlightedPaymentInstruments(new Set());
  };

  if (!firm || matterLoading) {
    return <LoadingView includeAnimation={true} />;
  }

  if (!matter) {
    return (
      <div className={styles.root}>
        <Breadcrumbs>
          <Anchor component={Link} to='/firms'>
            Firms
          </Anchor>
          <Anchor component={Link} to={`/firms/${firmId}`}>
            {firm.firmName}
          </Anchor>
          <Text>Matter Not Found</Text>
        </Breadcrumbs>
        <Title order={2} mt='xl'>
          Matter Not Found
        </Title>
        <Text mt='md' c='dimmed'>
          The matter with ID {matterId} could not be found.
        </Text>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'green';
      case 'closed':
        return 'gray';
      case 'new':
        return 'blue';
      case 'pendingEngagement':
        return 'yellow';
      case 'engagementLetterSent':
        return 'cyan';
      case 'earnedButNotClosed':
        return 'orange';
      case 'rejectedByAttorney':
        return 'red';
      default:
        return 'gray';
    }
  };

  return (
    <div className={styles.root}>
      <Breadcrumbs>
        <Anchor component={Link} to='/firms'>
          Firms
        </Anchor>
        <Anchor component={Link} to={`/firms/${firmId}`}>
          {firm.firmName}
        </Anchor>
        <Text>Matter: {matter.name}</Text>
      </Breadcrumbs>

      <Title order={2} mt='xl' mb='md'>
        {matter.name}
      </Title>

      {matter.description && (
        <Text size='lg' c='dimmed' mb='xl'>
          {matter.description}
        </Text>
      )}

      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} mb='xl'>
        <Paper shadow='sm' p='md' withBorder>
          <Text size='sm' c='dimmed' mb='xs'>
            Status
          </Text>
          <Badge size='lg' color={getStatusColor(matter.status)}>
            {matter.status}
          </Badge>
        </Paper>

        <Paper shadow='sm' p='md' withBorder>
          <Text size='sm' c='dimmed' mb='xs'>
            Matter Type
          </Text>
          <Text size='lg' fw={500}>
            {matter.matterType || '—'}
          </Text>
        </Paper>

        <Paper shadow='sm' p='md' withBorder>
          <Text size='sm' c='dimmed' mb='xs'>
            Practice Area
          </Text>
          <Text size='lg' fw={500}>
            {matter.practiceArea || '—'}
          </Text>
        </Paper>

        <Paper shadow='sm' p='md' withBorder>
          <Text size='sm' c='dimmed' mb='xs'>
            Responsible Attorney
          </Text>
          <Text size='lg' fw={500}>
            {matter.responsibleAttorney || '—'}
          </Text>
        </Paper>

        <Paper shadow='sm' p='md' withBorder>
          <Text size='sm' c='dimmed' mb='xs'>
            Created
          </Text>
          {matter.createdTimestamp ? (
            <Stack gap={4}>
              <Text size='lg' fw={500}>
                {formatRelativeDate(matter.createdTimestamp)}
              </Text>
              <Text size='xs' c='dimmed'>
                {new Date(matter.createdTimestamp).toLocaleString()}
              </Text>
            </Stack>
          ) : (
            <Text size='lg' fw={500}>
              —
            </Text>
          )}
        </Paper>

        <Paper shadow='sm' p='md' withBorder>
          <Text size='sm' c='dimmed' mb='xs'>
            Trust Balance
          </Text>
          <Text size='lg' fw={500}>
            {matter.trustBalance ? `$${matter.trustBalance.toFixed(2)}` : '—'}
          </Text>
        </Paper>
      </SimpleGrid>

      <Paper shadow='sm' p='md' withBorder>
        <Title order={3} mb='md'>
          Matter Details
        </Title>
        <Table>
          <Table.Tbody>
            <Table.Tr>
              <Table.Td fw={500}>Matter ID</Table.Td>
              <Table.Td>
                <Text ff='monospace' size='sm'>
                  {matter.id}
                </Text>
              </Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td fw={500}>State</Table.Td>
              <Table.Td>{matter.state || '—'}</Table.Td>
            </Table.Tr>
            {matter.otherAssignee && (
              <Table.Tr>
                <Table.Td fw={500}>Other Assignee</Table.Td>
                <Table.Td>{matter.otherAssignee}</Table.Td>
              </Table.Tr>
            )}
            {matter.flatFeeAmt && (
              <Table.Tr>
                <Table.Td fw={500}>Flat Fee Amount</Table.Td>
                <Table.Td>${matter.flatFeeAmt}</Table.Td>
              </Table.Tr>
            )}
            {matter.retainer && (
              <Table.Tr>
                <Table.Td fw={500}>Retainer</Table.Td>
                <Table.Td>${matter.retainer}</Table.Td>
              </Table.Tr>
            )}
            {matter.responsibleAttyHourlyRate && (
              <Table.Tr>
                <Table.Td fw={500}>Responsible Attorney Hourly Rate</Table.Td>
                <Table.Td>${matter.responsibleAttyHourlyRate}/hr</Table.Td>
              </Table.Tr>
            )}
            {matter.priorInvoicesBalance && (
              <Table.Tr>
                <Table.Td fw={500}>Prior Invoices Balance</Table.Td>
                <Table.Td>${matter.priorInvoicesBalance.toFixed(2)}</Table.Td>
              </Table.Tr>
            )}
            {matter.source && (
              <Table.Tr>
                <Table.Td fw={500}>Source</Table.Td>
                <Table.Td>
                  {matter.source}
                  {matter.sourceId && (
                    <Text size='xs' c='dimmed'>
                      {matter.sourceId}
                    </Text>
                  )}
                </Table.Td>
              </Table.Tr>
            )}
            {matter.engagementOriginator && (
              <Table.Tr>
                <Table.Td fw={500}>Engagement Originator</Table.Td>
                <Table.Td>{matter.engagementOriginator}</Table.Td>
              </Table.Tr>
            )}
            {matter.referralOriginator && (
              <Table.Tr>
                <Table.Td fw={500}>Referral Originator</Table.Td>
                <Table.Td>{matter.referralOriginator}</Table.Td>
              </Table.Tr>
            )}
            {matter.leadSourceId && (
              <Table.Tr>
                <Table.Td fw={500}>Lead Source ID</Table.Td>
                <Table.Td>{matter.leadSourceId}</Table.Td>
              </Table.Tr>
            )}
            {matter.scope && (
              <Table.Tr>
                <Table.Td fw={500}>Scope</Table.Td>
                <Table.Td>
                  <Text style={{ whiteSpace: 'pre-wrap' }}>{matter.scope}</Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      <Paper shadow='sm' p='md' withBorder mt='xl'>
        <Title order={3} mb='md'>
          Transactions{!transactionsLoading && ` (${transactions.length})`}
        </Title>
        {transactionsLoading ? (
          <Loader type='dots' size='lg' color='pink.4' />
        ) : transactions.length === 0 ? (
          <Text c='dimmed'>No transactions found for this matter.</Text>
        ) : (
          <Table striped highlightOnHover withTableBorder withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>ID</Table.Th>
                <Table.Th>Amount</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>Payment Method</Table.Th>
                <Table.Th>Source</Table.Th>
                <Table.Th>Created</Table.Th>
                <Table.Th>Completed</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {transactions.map((txn) => (
                <Table.Tr key={txn.id}>
                  <Table.Td>
                    <Text size='xs' ff='monospace'>
                      {txn.id}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size='sm' fw={500}>
                      ${(txn.amount ?? 0).toFixed(2)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <TransactionStatusBadge status={txn.status} />
                  </Table.Td>
                  <Table.Td>
                    <Text size='sm'>{txn.type || '—'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size='sm'>{txn.paymentMethod || '—'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size='sm'>{txn.source || '—'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size='sm'>
                      {txn.createdTimestamp
                        ? new Date(txn.createdTimestamp).toLocaleDateString()
                        : '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size='sm'>
                      {txn.completedTimestamp
                        ? new Date(txn.completedTimestamp).toLocaleDateString()
                        : '—'}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      {matter.finixIdentities && matter.finixIdentities.length > 0 && (
        <>
          <Paper shadow='sm' p='md' withBorder mt='xl'>
            <Title order={3} mb='md'>
              Finix Identities ({matter.finixIdentities!.length})
            </Title>
            <Accordion variant='separated'>
              {matter.finixIdentities.map((identity) => (
                <Accordion.Item key={identity.id} value={identity.id}>
                  <Accordion.Control>
                    <Group justify='space-between' wrap='nowrap' pr='md'>
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Text size='xs' ff='monospace' truncate>
                          {identity.id}
                        </Text>
                      </Box>
                      <Group gap='md' wrap='nowrap'>
                        {identity.identity_roles &&
                          identity.identity_roles.length > 0 && (
                            <Text size='sm'>
                              {identity.identity_roles.join(', ')}
                            </Text>
                          )}
                        {identity.entity?.business_address?.region && (
                          <Badge>
                            {identity.entity.business_address.region}
                          </Badge>
                        )}
                      </Group>
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap='xs'>
                      <Group justify='space-between'>
                        <Text size='sm' c='dimmed'>
                          Identity ID
                        </Text>
                        <Text size='sm' ff='monospace'>
                          {identity.id}
                        </Text>
                      </Group>

                      {identity.identity_roles &&
                        identity.identity_roles.length > 0 && (
                          <Group justify='space-between'>
                            <Text size='sm' c='dimmed'>
                              Roles
                            </Text>
                            <Text size='sm'>
                              {identity.identity_roles.join(', ')}
                            </Text>
                          </Group>
                        )}

                      {(identity as ExtendedIdentityFields).entity
                        ?.business_name && (
                        <Group justify='space-between'>
                          <Text size='sm' c='dimmed'>
                            Business Name
                          </Text>
                          <Text size='sm'>
                            {
                              (identity as ExtendedIdentityFields).entity!
                                .business_name
                            }
                          </Text>
                        </Group>
                      )}

                      {(identity as ExtendedIdentityFields).entity
                        ?.first_name && (
                        <Group justify='space-between'>
                          <Text size='sm' c='dimmed'>
                            First Name
                          </Text>
                          <Text size='sm'>
                            {
                              (identity as ExtendedIdentityFields).entity!
                                .first_name
                            }
                          </Text>
                        </Group>
                      )}

                      {(identity as ExtendedIdentityFields).entity
                        ?.last_name && (
                        <Group justify='space-between'>
                          <Text size='sm' c='dimmed'>
                            Last Name
                          </Text>
                          <Text size='sm'>
                            {
                              (identity as ExtendedIdentityFields).entity!
                                .last_name
                            }
                          </Text>
                        </Group>
                      )}

                      {(identity as ExtendedIdentityFields).entity?.email && (
                        <Group justify='space-between'>
                          <Text size='sm' c='dimmed'>
                            Email
                          </Text>
                          <Text size='sm'>
                            {(identity as ExtendedIdentityFields).entity!.email}
                          </Text>
                        </Group>
                      )}

                      {(identity as ExtendedIdentityFields).entity?.phone && (
                        <Group justify='space-between'>
                          <Text size='sm' c='dimmed'>
                            Phone
                          </Text>
                          <Text size='sm'>
                            {(identity as ExtendedIdentityFields).entity!.phone}
                          </Text>
                        </Group>
                      )}

                      {(identity as ExtendedIdentityFields).entity
                        ?.business_address && (
                        <>
                          {(identity as ExtendedIdentityFields).entity!
                            .business_address!.line1 && (
                            <Group justify='space-between' align='flex-start'>
                              <Text size='sm' c='dimmed'>
                                Address
                              </Text>
                              <Stack gap={4} align='flex-end'>
                                <Text size='sm'>
                                  {
                                    (identity as ExtendedIdentityFields).entity!
                                      .business_address!.line1
                                  }
                                </Text>
                                {(identity as ExtendedIdentityFields).entity!
                                  .business_address!.line2 && (
                                  <Text size='sm'>
                                    {
                                      (identity as ExtendedIdentityFields)
                                        .entity!.business_address!.line2
                                    }
                                  </Text>
                                )}
                                <Text size='sm'>
                                  {
                                    (identity as ExtendedIdentityFields).entity!
                                      .business_address!.city
                                  }
                                  {(identity as ExtendedIdentityFields).entity!
                                    .business_address!.region &&
                                    `, ${
                                      (identity as ExtendedIdentityFields)
                                        .entity!.business_address!.region
                                    }`}
                                  {(identity as ExtendedIdentityFields).entity!
                                    .business_address!.postal_code &&
                                    ` ${
                                      (identity as ExtendedIdentityFields)
                                        .entity!.business_address!.postal_code
                                    }`}
                                </Text>
                                {(identity as ExtendedIdentityFields).entity!
                                  .business_address!.country && (
                                  <Text size='sm'>
                                    {
                                      (identity as ExtendedIdentityFields)
                                        .entity!.business_address!.country
                                    }
                                  </Text>
                                )}
                              </Stack>
                            </Group>
                          )}
                        </>
                      )}

                      {(identity as ExtendedIdentityFields).entity
                        ?.business_tax_id && (
                        <Group justify='space-between'>
                          <Text size='sm' c='dimmed'>
                            Tax ID
                          </Text>
                          <Text size='sm' ff='monospace'>
                            {
                              (identity as ExtendedIdentityFields).entity!
                                .business_tax_id
                            }
                          </Text>
                        </Group>
                      )}

                      {(identity as ExtendedIdentityFields).entity
                        ?.incorporation_date && (
                        <Group justify='space-between'>
                          <Text size='sm' c='dimmed'>
                            Incorporation Date
                          </Text>
                          <Text size='sm'>
                            {
                              (identity as ExtendedIdentityFields).entity!
                                .incorporation_date
                            }
                          </Text>
                        </Group>
                      )}

                      {identity.tags &&
                        Object.keys(identity.tags).length > 0 && (
                          <Group justify='space-between' align='flex-start'>
                            <Text size='sm' c='dimmed'>
                              Tags
                            </Text>
                            <Stack gap={4} align='flex-end'>
                              {Object.entries(identity.tags).map(
                                ([key, value]) => (
                                  <Text key={key} size='sm'>
                                    {key}: {value}
                                  </Text>
                                ),
                              )}
                            </Stack>
                          </Group>
                        )}

                      {identity.created_at && (
                        <Group justify='space-between'>
                          <Text size='sm' c='dimmed'>
                            Created At
                          </Text>
                          <Text size='sm'>
                            {new Date(identity.created_at).toLocaleDateString()}
                          </Text>
                        </Group>
                      )}

                      {(identity as ExtendedIdentityFields).updated_at && (
                        <Group justify='space-between'>
                          <Text size='sm' c='dimmed'>
                            Updated At
                          </Text>
                          <Text size='sm'>
                            {new Date(
                              (identity as ExtendedIdentityFields).updated_at!,
                            ).toLocaleDateString()}
                          </Text>
                        </Group>
                      )}

                      {(identity as ExtendedIdentityFields).application && (
                        <Group justify='space-between'>
                          <Text size='sm' c='dimmed'>
                            Application
                          </Text>
                          <Text size='sm' ff='monospace'>
                            {(identity as ExtendedIdentityFields).application}
                          </Text>
                        </Group>
                      )}
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          </Paper>

          {matter.finixIdentities.some(
            (identity) => identity.paymentInstruments?.length > 0,
          ) && (
            <Paper shadow='sm' p='md' withBorder mt='xl'>
              <Group justify='space-between' align='center' mb='md'>
                <Title order={3}>Finix Payment Instruments</Title>
                {highlightedPaymentInstruments.size > 0 && (
                  <Button
                    variant='subtle'
                    size='xs'
                    leftSection={<XIcon size={14} />}
                    onClick={clearAllHighlights}
                  >
                    Clear ({highlightedPaymentInstruments.size})
                  </Button>
                )}
              </Group>
              <Accordion variant='separated'>
                {matter.finixIdentities.flatMap((identity) =>
                  (identity.paymentInstruments || []).map((instrument) => {
                    const isHighlighted = highlightedPaymentInstruments.has(
                      instrument.id,
                    );
                    return (
                      <Accordion.Item
                        key={instrument.id}
                        value={instrument.id}
                        style={{
                          backgroundColor: isHighlighted
                            ? 'rgba(34, 139, 230, 0.1)'
                            : undefined,
                          border: isHighlighted
                            ? '2px solid rgba(34, 139, 230, 0.5)'
                            : undefined,
                          borderRadius: '8px',
                        }}
                      >
                        <Accordion.Control>
                          <Group justify='space-between' wrap='nowrap' pr='md'>
                            <Box style={{ flex: 1, minWidth: 0 }}>
                              <Group gap='xs'>
                                <Badge>{instrument.instrument_type}</Badge>
                                {instrument.last_four && (
                                  <Text size='sm'>
                                    ****{instrument.last_four}
                                  </Text>
                                )}
                              </Group>
                            </Box>
                            <Group gap='xs' wrap='nowrap'>
                              <Tooltip
                                label={
                                  isHighlighted
                                    ? 'Remove filter'
                                    : 'Filter transfers'
                                }
                                position='left'
                              >
                                <ActionIcon
                                  variant={isHighlighted ? 'filled' : 'subtle'}
                                  color={isHighlighted ? 'blue' : 'gray'}
                                  size='sm'
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    togglePaymentInstrumentHighlight(
                                      instrument.id,
                                    );
                                  }}
                                >
                                  <TargetIcon size={16} />
                                </ActionIcon>
                              </Tooltip>
                              <Badge
                                color={instrument.enabled ? 'green' : 'red'}
                                variant='light'
                              >
                                {instrument.enabled ? 'Enabled' : 'Disabled'}
                              </Badge>
                            </Group>
                          </Group>
                        </Accordion.Control>
                        <Accordion.Panel>
                          <Stack gap='xs'>
                            <Group justify='space-between'>
                              <Text size='sm' c='dimmed'>
                                Instrument ID
                              </Text>
                              <Text size='sm' ff='monospace'>
                                {instrument.id}
                              </Text>
                            </Group>

                            <Group justify='space-between'>
                              <Text size='sm' c='dimmed'>
                                Type
                              </Text>
                              <Badge>{instrument.instrument_type}</Badge>
                            </Group>

                            {instrument.brand && (
                              <Group justify='space-between'>
                                <Text size='sm' c='dimmed'>
                                  Brand
                                </Text>
                                <Text size='sm'>{instrument.brand}</Text>
                              </Group>
                            )}

                            {instrument.last_four && (
                              <Group justify='space-between'>
                                <Text size='sm' c='dimmed'>
                                  Last Four
                                </Text>
                                <Text size='sm' ff='monospace'>
                                  ****{instrument.last_four}
                                </Text>
                              </Group>
                            )}

                            {instrument.name && (
                              <Group justify='space-between'>
                                <Text size='sm' c='dimmed'>
                                  Name
                                </Text>
                                <Text size='sm'>{instrument.name}</Text>
                              </Group>
                            )}

                            {instrument.account_type && (
                              <Group justify='space-between'>
                                <Text size='sm' c='dimmed'>
                                  Account Type
                                </Text>
                                <Text size='sm'>{instrument.account_type}</Text>
                              </Group>
                            )}

                            {instrument.expiration_month &&
                              instrument.expiration_year && (
                                <Group justify='space-between'>
                                  <Text size='sm' c='dimmed'>
                                    Expiration
                                  </Text>
                                  <Text size='sm'>
                                    {instrument.expiration_month}/
                                    {instrument.expiration_year}
                                  </Text>
                                </Group>
                              )}

                            <Group justify='space-between'>
                              <Text size='sm' c='dimmed'>
                                Enabled
                              </Text>
                              <Badge
                                color={instrument.enabled ? 'green' : 'red'}
                              >
                                {instrument.enabled ? 'Enabled' : 'Disabled'}
                              </Badge>
                            </Group>

                            {instrument.identity && (
                              <Group justify='space-between'>
                                <Text size='sm' c='dimmed'>
                                  Identity
                                </Text>
                                <Text size='sm' ff='monospace'>
                                  {instrument.identity}
                                </Text>
                              </Group>
                            )}

                            {(instrument as ExtendedPaymentInstrumentFields)
                              .fingerprint && (
                              <Group justify='space-between'>
                                <Text size='sm' c='dimmed'>
                                  Fingerprint
                                </Text>
                                <Text size='sm' ff='monospace'>
                                  {
                                    (
                                      instrument as ExtendedPaymentInstrumentFields
                                    ).fingerprint
                                  }
                                </Text>
                              </Group>
                            )}

                            {(instrument as ExtendedPaymentInstrumentFields)
                              .bank_code && (
                              <Group justify='space-between'>
                                <Text size='sm' c='dimmed'>
                                  Bank Code
                                </Text>
                                <Text size='sm'>
                                  {
                                    (
                                      instrument as ExtendedPaymentInstrumentFields
                                    ).bank_code
                                  }
                                </Text>
                              </Group>
                            )}

                            {(instrument as ExtendedPaymentInstrumentFields)
                              .country && (
                              <Group justify='space-between'>
                                <Text size='sm' c='dimmed'>
                                  Country
                                </Text>
                                <Text size='sm'>
                                  {
                                    (
                                      instrument as ExtendedPaymentInstrumentFields
                                    ).country
                                  }
                                </Text>
                              </Group>
                            )}

                            {(instrument as ExtendedPaymentInstrumentFields)
                              .currency && (
                              <Group justify='space-between'>
                                <Text size='sm' c='dimmed'>
                                  Currency
                                </Text>
                                <Text size='sm'>
                                  {
                                    (
                                      instrument as ExtendedPaymentInstrumentFields
                                    ).currency
                                  }
                                </Text>
                              </Group>
                            )}

                            {instrument.tags &&
                              Object.keys(instrument.tags).length > 0 && (
                                <Group
                                  justify='space-between'
                                  align='flex-start'
                                >
                                  <Text size='sm' c='dimmed'>
                                    Tags
                                  </Text>
                                  <Stack gap={4} align='flex-end'>
                                    {Object.entries(instrument.tags).map(
                                      ([key, value]) => (
                                        <Text key={key} size='sm'>
                                          {key}: {value}
                                        </Text>
                                      ),
                                    )}
                                  </Stack>
                                </Group>
                              )}

                            <Group justify='space-between'>
                              <Text size='sm' c='dimmed'>
                                Created At
                              </Text>
                              <Text size='sm'>
                                {new Date(
                                  instrument.created_at,
                                ).toLocaleDateString()}
                              </Text>
                            </Group>

                            {(instrument as ExtendedPaymentInstrumentFields)
                              .updated_at && (
                              <Group justify='space-between'>
                                <Text size='sm' c='dimmed'>
                                  Updated At
                                </Text>
                                <Text size='sm'>
                                  {new Date(
                                    (
                                      instrument as ExtendedPaymentInstrumentFields
                                    ).updated_at!,
                                  ).toLocaleDateString()}
                                </Text>
                              </Group>
                            )}

                            {(instrument as ExtendedPaymentInstrumentFields)
                              .application && (
                              <Group justify='space-between'>
                                <Text size='sm' c='dimmed'>
                                  Application
                                </Text>
                                <Text size='sm' ff='monospace'>
                                  {
                                    (
                                      instrument as ExtendedPaymentInstrumentFields
                                    ).application
                                  }
                                </Text>
                              </Group>
                            )}
                          </Stack>
                        </Accordion.Panel>
                      </Accordion.Item>
                    );
                  }),
                )}
              </Accordion>
            </Paper>
          )}

          {matter.finixIdentities.some(
            (identity) => identity.transfers?.length > 0,
          ) && (
            <Paper shadow='sm' p='md' withBorder mt='xl'>
              <Title order={3} mb='md'>
                Finix Transfers
              </Title>
              <Accordion variant='separated'>
                {matter.finixIdentities.flatMap((identity) =>
                  (identity.transfers || []).map((transfer) => {
                    const shouldDim =
                      highlightedPaymentInstruments.size > 0 &&
                      (!transfer.source ||
                        !highlightedPaymentInstruments.has(transfer.source));
                    return (
                      <Accordion.Item
                        key={transfer.id}
                        value={transfer.id}
                        style={{
                          opacity: shouldDim ? 0.3 : 1,
                          transition: 'opacity 0.2s ease',
                        }}
                      >
                        <Accordion.Control>
                          <Group justify='space-between' wrap='nowrap' pr='md'>
                            <Box style={{ flex: 1, minWidth: 0 }}>
                              <Text size='xs' ff='monospace' truncate>
                                {transfer.id}
                              </Text>
                            </Box>
                            <Group gap='md' wrap='nowrap'>
                              <Text size='sm' fw={600}>
                                ${(transfer.amount / 100).toFixed(2)}
                              </Text>
                              <TransferStateBadge state={transfer.state} />
                            </Group>
                          </Group>
                        </Accordion.Control>
                        <Accordion.Panel>
                          <Stack gap='xs'>
                            <Group justify='space-between'>
                              <Text size='sm' c='dimmed'>
                                Transfer ID
                              </Text>
                              <Text size='sm' ff='monospace'>
                                {transfer.id}
                              </Text>
                            </Group>

                            <Group justify='space-between'>
                              <Text size='sm' c='dimmed'>
                                Amount
                              </Text>
                              <Text size='sm' fw={500}>
                                ${(transfer.amount / 100).toFixed(2)}
                              </Text>
                            </Group>

                            {transfer.fee !== undefined &&
                              transfer.fee !== null && (
                                <Group justify='space-between'>
                                  <Text size='sm' c='dimmed'>
                                    Fee
                                  </Text>
                                  <Text size='sm'>
                                    ${(transfer.fee / 100).toFixed(2)}
                                  </Text>
                                </Group>
                              )}

                            <Group justify='space-between'>
                              <Text size='sm' c='dimmed'>
                                State
                              </Text>
                              <TransferStateBadge state={transfer.state} />
                            </Group>

                            {transfer.ready_to_settle_at && (
                              <Group justify='space-between'>
                                <Text size='sm' c='dimmed'>
                                  Ready to Settle
                                </Text>
                                <Text size='sm'>
                                  {new Date(
                                    transfer.ready_to_settle_at,
                                  ).toLocaleDateString()}
                                </Text>
                              </Group>
                            )}

                            <Group justify='space-between'>
                              <Text size='sm' c='dimmed'>
                                Created At
                              </Text>
                              <Text size='sm'>
                                {new Date(
                                  transfer.created_at,
                                ).toLocaleDateString()}
                              </Text>
                            </Group>

                            {(transfer as ExtendedTransferFields)
                              .updated_at && (
                              <Group justify='space-between'>
                                <Text size='sm' c='dimmed'>
                                  Updated At
                                </Text>
                                <Text size='sm'>
                                  {new Date(
                                    (transfer as ExtendedTransferFields)
                                      .updated_at!,
                                  ).toLocaleDateString()}
                                </Text>
                              </Group>
                            )}

                            {(transfer as ExtendedTransferFields).currency && (
                              <Group justify='space-between'>
                                <Text size='sm' c='dimmed'>
                                  Currency
                                </Text>
                                <Text size='sm'>
                                  {
                                    (transfer as ExtendedTransferFields)
                                      .currency
                                  }
                                </Text>
                              </Group>
                            )}

                            {transfer.destination && (
                              <Group justify='space-between'>
                                <Text size='sm' c='dimmed'>
                                  Destination
                                </Text>
                                <Text size='sm' ff='monospace'>
                                  {transfer.destination}
                                </Text>
                              </Group>
                            )}

                            {transfer.source && (
                              <Group justify='space-between'>
                                <Text size='sm' c='dimmed'>
                                  Source
                                </Text>
                                <Text size='sm' ff='monospace'>
                                  {transfer.source}
                                </Text>
                              </Group>
                            )}

                            {transfer.merchant_identity && (
                              <Group justify='space-between'>
                                <Text size='sm' c='dimmed'>
                                  Merchant Identity
                                </Text>
                                <Text size='sm' ff='monospace'>
                                  {transfer.merchant_identity}
                                </Text>
                              </Group>
                            )}

                            {transfer.trace_id && (
                              <Group justify='space-between'>
                                <Text size='sm' c='dimmed'>
                                  Trace ID
                                </Text>
                                <Text size='sm' ff='monospace'>
                                  {transfer.trace_id}
                                </Text>
                              </Group>
                            )}

                            {(transfer as ExtendedTransferFields)
                              .failure_code && (
                              <Group justify='space-between'>
                                <Text size='sm' c='dimmed'>
                                  Failure Code
                                </Text>
                                <Badge color='red'>
                                  {
                                    (transfer as ExtendedTransferFields)
                                      .failure_code
                                  }
                                </Badge>
                              </Group>
                            )}

                            {(transfer as ExtendedTransferFields)
                              .failure_message && (
                              <Group justify='space-between'>
                                <Text size='sm' c='dimmed'>
                                  Failure Message
                                </Text>
                                <Text size='sm'>
                                  {
                                    (transfer as ExtendedTransferFields)
                                      .failure_message
                                  }
                                </Text>
                              </Group>
                            )}

                            {transfer.tags &&
                              Object.keys(transfer.tags).length > 0 && (
                                <Group
                                  justify='space-between'
                                  align='flex-start'
                                >
                                  <Text size='sm' c='dimmed'>
                                    Tags
                                  </Text>
                                  <Stack gap={4} align='flex-end'>
                                    {Object.entries(transfer.tags).map(
                                      ([key, value]) => (
                                        <Text key={key} size='sm'>
                                          {key}: {value}
                                        </Text>
                                      ),
                                    )}
                                  </Stack>
                                </Group>
                              )}

                            {(transfer as ExtendedTransferFields)
                              .application && (
                              <Group justify='space-between'>
                                <Text size='sm' c='dimmed'>
                                  Application
                                </Text>
                                <Text size='sm' ff='monospace'>
                                  {
                                    (transfer as ExtendedTransferFields)
                                      .application
                                  }
                                </Text>
                              </Group>
                            )}

                            {(transfer as ExtendedTransferFields)
                              .statement_descriptor && (
                              <Group justify='space-between'>
                                <Text size='sm' c='dimmed'>
                                  Statement Descriptor
                                </Text>
                                <Text size='sm'>
                                  {
                                    (transfer as ExtendedTransferFields)
                                      .statement_descriptor
                                  }
                                </Text>
                              </Group>
                            )}

                            {(transfer as ExtendedTransferFields).type && (
                              <Group justify='space-between'>
                                <Text size='sm' c='dimmed'>
                                  Type
                                </Text>
                                <Text size='sm'>
                                  {(transfer as ExtendedTransferFields).type}
                                </Text>
                              </Group>
                            )}

                            {transfer.merchant && (
                              <Group justify='space-between'>
                                <Text size='sm' c='dimmed'>
                                  Merchant
                                </Text>
                                <Text size='sm' ff='monospace'>
                                  {transfer.merchant}
                                </Text>
                              </Group>
                            )}

                            <TransferWebhookEvents
                              transferId={transfer.id}
                              webhookEvents={webhookEvents}
                            />
                          </Stack>
                        </Accordion.Panel>
                      </Accordion.Item>
                    );
                  }),
                )}
              </Accordion>
            </Paper>
          )}
        </>
      )}
    </div>
  );
};

export default MatterDetail;

const TransactionStatusBadge = ({
  status,
}: {
  status: TransactionDocument['status'];
}) => {
  switch (status) {
    case 'succeeded':
      return <Badge color='green'>Succeeded</Badge>;
    case 'failed':
      return <Badge color='red'>Failed</Badge>;
    case 'returned':
      return <Badge color='red'>Returned</Badge>;
    case 'cancelled':
      return <Badge color='gray'>Cancelled</Badge>;
    case 'pending':
      return <Badge color='yellow'>Pending</Badge>;
    case 'draft':
      return <Badge color='blue'>Draft</Badge>;
    default:
      return <Badge color='gray'>{status || 'Unknown'}</Badge>;
  }
};

const TransferStateBadge = ({ state }: { state: string }) => {
  switch (state.toLowerCase()) {
    case 'succeeded':
      return <Badge color='green'>Completed</Badge>;
    case 'failed':
      return <Badge color='red'>Failed</Badge>;
    case 'returned':
      return <Badge color='red'>Returned</Badge>;
    default:
      return <Badge color='blue'>{state}</Badge>;
  }
};

interface TransferWebhookEventsProps {
  transferId: string;
  webhookEvents: Array<{
    id?: string;
    entity?: string;
    type?: string;
    occurred_at?: string;
    _embedded?: Record<string, unknown>;
    _embedded_ids?: string[];
  }>;
}

interface EmbeddedTransfer {
  id?: string;
  type?: string;
  state?: string;
  parent_transfer?: string;
}

const WebhookEventsTable = ({
  label,
  events,
}: {
  label: string;
  events: TransferWebhookEventsProps['webhookEvents'];
}) => {
  if (events.length === 0) {
    return null;
  }

  return (
    <Box mt='md'>
      <Text size='sm' fw={600} c='dimmed' mb='xs'>
        {label} ({events.length})
      </Text>
      <Table withTableBorder withColumnBorders>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Occurred At</Table.Th>
            <Table.Th>Event Type</Table.Th>
            <Table.Th>Transfer Type</Table.Th>
            <Table.Th>Transfer State</Table.Th>
            <Table.Th>ID</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {events.map((event) => {
            const embeddedTransfer = (
              event._embedded as Record<string, EmbeddedTransfer[]> | undefined
            )?.transfers?.[0];
            return (
              <Table.Tr key={event.id}>
                <Table.Td>{event.occurred_at}</Table.Td>
                <Table.Td>{event.type}</Table.Td>
                <Table.Td>{embeddedTransfer?.type}</Table.Td>
                <Table.Td>{embeddedTransfer?.state}</Table.Td>
                <Table.Td>
                  <Anchor
                    href={`/firestore-browser/finixWebhookEvents/${event.id}`}
                    target='_blank'
                  >
                    {event.id}
                  </Anchor>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Box>
  );
};

const TransferWebhookEvents = ({
  transferId,
  webhookEvents,
}: TransferWebhookEventsProps) => {
  const sortByOccurredAt = (
    a: TransferWebhookEventsProps['webhookEvents'][number],
    b: TransferWebhookEventsProps['webhookEvents'][number],
  ) =>
    new Date(a.occurred_at ?? '').getTime() -
    new Date(b.occurred_at ?? '').getTime();

  const transferEvents = webhookEvents
    .filter(
      (e) =>
        e.entity === 'transfer' && (e._embedded_ids ?? []).includes(transferId),
    )
    .sort(sortByOccurredAt);

  const childEventsByEntityId = new Map<
    string,
    TransferWebhookEventsProps['webhookEvents']
  >();
  for (const event of webhookEvents) {
    if (event.entity !== 'transfer') continue;
    const embeddedTransfer = (
      event._embedded as Record<string, EmbeddedTransfer[]> | undefined
    )?.transfers?.[0];
    if (embeddedTransfer?.parent_transfer !== transferId) continue;
    if (embeddedTransfer?.id === transferId) continue;
    const childId = embeddedTransfer?.id;
    if (!childId) continue;
    const existing = childEventsByEntityId.get(childId) ?? [];
    existing.push(event);
    childEventsByEntityId.set(childId, existing);
  }

  if (transferEvents.length === 0 && childEventsByEntityId.size === 0) {
    return null;
  }

  return (
    <>
      <WebhookEventsTable label='Webhook Events' events={transferEvents} />
      {[...childEventsByEntityId.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([childId, events]) => (
          <WebhookEventsTable
            key={childId}
            label={`Child ${childId} Webhook Events`}
            events={events.sort(sortByOccurredAt)}
          />
        ))}
    </>
  );
};

// const getWebhookEventsForTransfer = async (props: { db: Firestore, transferId: string }) => {
//   const { db, transferId } = props;
//   db.collection('finixWebhookEvents').where('sourceTransactionId', '==', transferId).get();
//   const webhookEvents = await
//   return webhookEvents;
// };
